import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, 
  Send, 
  Smile, 
  MoreVertical, 
  Shield, 
  Ban, 
  Crown,
  Heart,
  ThumbsUp,
  Gift
} from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { ToastContext } from '../ToastProvider';

export default function LiveChat({ 
  sessionId, 
  affiliateId, 
  isLive, 
  onMessageCountChange 
}) {
  const { user } = useAuth();
  const { notify } = React.useContext(ToastContext);
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [moderationMode, setModerationMode] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState(new Set());
  
  const chatRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Mock WebSocket connection (replace with real WebSocket)
  useEffect(() => {
    if (isLive && sessionId) {
      connectToChat();
    } else {
      disconnectFromChat();
    }
    
    return () => {
      disconnectFromChat();
    };
  }, [isLive, sessionId]);

  const connectToChat = () => {
    // Mock WebSocket connection
    setIsConnected(true);
    setUserCount(Math.floor(Math.random() * 50) + 10);
    
    // Simulate receiving messages
    const messageInterval = setInterval(() => {
      if (isLive) {
        const mockMessages = [
          { id: Date.now(), user: 'Viewer123', text: 'Great products!', timestamp: new Date(), type: 'message' },
          { id: Date.now() + 1, user: 'Customer456', text: 'How much is shipping?', timestamp: new Date(), type: 'message' },
          { id: Date.now() + 2, user: 'Fan789', text: 'Love this live stream!', timestamp: new Date(), type: 'message' },
          { id: Date.now() + 3, user: 'Buyer101', text: 'Can I get a discount?', timestamp: new Date(), type: 'message' }
        ];
        
        const randomMessage = mockMessages[Math.floor(Math.random() * mockMessages.length)];
        addMessage(randomMessage);
      } else {
        clearInterval(messageInterval);
      }
    }, 3000);
  };

  const disconnectFromChat = () => {
    setIsConnected(false);
    setUserCount(0);
  };

  const addMessage = (message) => {
    setMessages(prev => {
      const newMessages = [...prev, message].slice(-100); // Keep last 100 messages
      onMessageCountChange?.(newMessages.length);
      return newMessages;
    });
    
    // Auto-scroll to bottom
    setTimeout(() => {
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
    }, 100);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !user) return;

    const message = {
      id: Date.now(),
      userId: user.uid,
      user: user.displayName || user.email?.split('@')[0] || 'Anonymous',
      text: newMessage.trim(),
      timestamp: new Date(),
      type: 'message',
      isOwner: true
    };

    // Add message immediately for better UX
    addMessage(message);
    setNewMessage('');

    // Simulate sending to server
    try {
      // Here you would send to your WebSocket server
      notify('Message sent!', { type: 'success' });
    } catch (error) {
      notify('Failed to send message', { type: 'error' });
    }
  };

  const sendReaction = (reaction) => {
    if (!user) return;

    const reactionMessage = {
      id: Date.now(),
      userId: user.uid,
      user: user.displayName || user.email?.split('@')[0] || 'Anonymous',
      text: reaction,
      timestamp: new Date(),
      type: 'reaction',
      isOwner: true
    };

    addMessage(reactionMessage);
  };

  const moderateMessage = (messageId, action) => {
    if (action === 'delete') {
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      notify('Message deleted', { type: 'info' });
    } else if (action === 'block') {
      const message = messages.find(msg => msg.id === messageId);
      if (message) {
        setBlockedUsers(prev => new Set([...prev, message.userId]));
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        notify(`User ${message.user} blocked`, { type: 'warning' });
      }
    }
  };

  const getMessageIcon = (message) => {
    if (message.type === 'reaction') {
      switch (message.text) {
        case '❤️': return <Heart className="w-4 h-4 text-red-500" />;
        case '👍': return <ThumbsUp className="w-4 h-4 text-blue-500" />;
        case '🎁': return <Gift className="w-4 h-4 text-purple-500" />;
        default: return <Smile className="w-4 h-4 text-yellow-500" />;
      }
    }
    return null;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-PH', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const emojis = ['❤️', '👍', '🎁', '🔥', '💯', '😍', '🤩', '👏', '🎉', '💪'];

  return (
    <div className="w-80 border-l border-gray-200 flex flex-col bg-white">
      {/* Chat Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MessageCircle className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Live Chat</h3>
            {isConnected && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-600">{userCount} online</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-1">
            {user && (
              <button
                onClick={() => setModerationMode(!moderationMode)}
                className={`p-1 rounded ${moderationMode ? 'bg-red-100 text-red-600' : 'text-gray-400 hover:text-gray-600'}`}
                title="Moderation Mode"
              >
                <Shield className="w-4 h-4" />
              </button>
            )}
            <button className="p-1 text-gray-400 hover:text-gray-600">
              <MoreVertical className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div 
        ref={chatRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
      >
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs text-gray-400">Start chatting with your viewers!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`group relative ${
                message.isOwner ? 'ml-8' : 'mr-8'
              }`}
            >
              <div
                className={`p-3 rounded-lg ${
                  message.type === 'reaction'
                    ? 'bg-yellow-100 border border-yellow-200'
                    : message.isOwner
                    ? 'bg-blue-100 text-blue-900'
                    : 'bg-white border border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-sm">
                      {message.isOwner && <Crown className="w-3 h-3 text-yellow-500 inline mr-1" />}
                      {message.user}
                    </span>
                    {getMessageIcon(message)}
                  </div>
                  <span className="text-xs opacity-70">
                    {formatTime(message.timestamp)}
                  </span>
                </div>
                <p className="text-sm">{message.text}</p>
              </div>
              
              {/* Moderation Controls */}
              {moderationMode && !message.isOwner && (
                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => moderateMessage(message.id, 'delete')}
                      className="p-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                      title="Delete Message"
                    >
                      <Ban className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => moderateMessage(message.id, 'block')}
                      className="p-1 bg-orange-100 text-orange-600 rounded hover:bg-orange-200"
                      title="Block User"
                    >
                      <Shield className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Message Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        {/* Emoji Picker */}
        {showEmojiPicker && (
          <div className="mb-3 p-3 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-5 gap-2">
              {emojis.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    sendReaction(emoji);
                    setShowEmojiPicker(false);
                  }}
                  className="p-2 hover:bg-gray-200 rounded text-lg"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex space-x-2">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Smile className="w-5 h-5" />
          </button>
          
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Type a message..."
            className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!isConnected}
          />
          
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || !isConnected}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        
        {!isConnected && (
          <p className="text-xs text-gray-500 mt-2 text-center">
            Chat will be available when live stream starts
          </p>
        )}
      </div>
    </div>
  );
}
